import React, { startTransition, useCallback, useState } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'
import { factoriesState, userState } from '../../../states/userState'
import classes from './FactorySelector.module.scss'
import { factories as factoryList } from '../../../commons/factories'
import { foreColorCode, foreColorPagination } from '../../../commons/colors'
import { Divider } from '@mui/material'

const FactorySelector = () => {
  const [factories, setFactories] = useRecoilState(factoriesState)
  const [factoriesForUi, setFactoriesForUi] = useState(factories)
  const user = useRecoilValue(userState)

  // 「本社工場」のIDを抽出 (見つからなければundefined)
  const headquartersId = factoryList.find((f) => f.name === '本社工場')?.id

  // 全工場と外注工場のIDリスト
  const actualFactoryIds = factoryList.map((f) => f.id).filter((id) => id !== 0)
  const outsourceFactoryIds = [58, 59, 62, 68]

  // 「全工場」ボタン・「外注」ボタンの選択状態判定用
  const isAllFactoriesSelected = actualFactoryIds.every((id) =>
    factories.includes(id)
  )
  const isOutsourceSelected = outsourceFactoryIds.every((id) =>
    factories.includes(id)
  )
  const isAllFactoriesForUiSelected = actualFactoryIds.every((id) =>
    factoriesForUi.includes(id)
  )
  const isOutsourceForUiSelected = outsourceFactoryIds.every((id) =>
    factoriesForUi.includes(id)
  )

  const handleToggleFactory = useCallback(
    (id) => {
      // UIの更新
      setFactoriesForUi((prev) => {
        let updated

        if (id === 0) {
          // 全工場切替
          updated = isAllFactoriesForUiSelected ? [] : actualFactoryIds
        } else if (outsourceFactoryIds.includes(id)) {
          // 外注ボタン切替
          updated = isOutsourceForUiSelected
            ? prev.filter(
                (factoryId) => !outsourceFactoryIds.includes(factoryId)
              )
            : [...prev, ...outsourceFactoryIds]
        } else {
          // 通常の工場切替
          updated = prev.includes(id)
            ? prev.filter((factoryId) => factoryId !== id)
            : [...prev, id]
        }
        // 最低1つは選択を維持
        if (updated.length === 0 && headquartersId !== undefined) {
          updated = [headquartersId]
        }
        return updated
      })

      // recoilの更新
      startTransition(() => {
        setFactories((prev) => {
          let updated

          if (id === 0) {
            updated = isAllFactoriesSelected ? [] : actualFactoryIds
          } else if (outsourceFactoryIds.includes(id)) {
            updated = isOutsourceSelected
              ? prev.filter(
                  (factoryId) => !outsourceFactoryIds.includes(factoryId)
                )
              : [...prev, ...outsourceFactoryIds]
          } else {
            updated = prev.includes(id)
              ? prev.filter((factoryId) => factoryId !== id)
              : [...prev, id]
          }
          // 最低1つは選択を維持
          if (updated.length === 0 && headquartersId !== undefined) {
            updated = [headquartersId]
          }
          return updated
        })
      })
    },
    [
      isAllFactoriesForUiSelected,
      isAllFactoriesSelected,
      isOutsourceForUiSelected,
      isOutsourceSelected,
      setFactoriesForUi,
      setFactories,
      actualFactoryIds,
      outsourceFactoryIds,
      headquartersId,
    ]
  )

  return (
    <div className={classes.root}>
      <div className={classes.mainContainer}>
        <p
          className={classes.name}
          style={foreColorPagination(user?.mode ?? 'lite')}
        >
          製作工場
        </p>
        <Divider
          textAlign="left"
          sx={dividerStyle(
            user?.mode === 'lite'
              ? foreColorCode('lite')
              : foreColorCode('dark')
          )}
          variant="fullWidth"
        />
        <div className={classes.buttons}>
          {factoryList.map((factory) => {
            if (factory.id === 0) {
              // 全工場ボタン
              return (
                <div
                  key={factory.id}
                  className={
                    isAllFactoriesForUiSelected
                      ? classes.buttonAllSelected
                      : classes.buttonAll
                  }
                  onClick={() => handleToggleFactory(factory.id)}
                >
                  {factory.name}
                </div>
              )
            }

            // 外注IDは個別ボタンを非表示にし、共通で「外注」ボタン表示
            if (outsourceFactoryIds.includes(factory.id)) return null

            const isActive = factoriesForUi.includes(factory.id)
            return (
              <div
                key={factory.id}
                className={
                  isActive ? classes.buttonActive : classes.buttonInactive
                }
                onClick={() => handleToggleFactory(factory.id)}
              >
                {factory.name}
              </div>
            )
          })}

          {/* 外注ボタン */}
          <div
            className={
              isOutsourceSelected
                ? classes.buttonOutsourceSelected
                : classes.buttonInactive
            }
            onClick={() => handleToggleFactory(58)}
          >
            外注
          </div>
        </div>
      </div>
    </div>
  )
}

const dividerStyle = (color) => ({
  borderColor: `${color}`, // 線の色
  fontSize: '18px',
  marginBottom: '10px',
  '&::before, &::after': {
    borderTopColor: `${color}`, // 横線の色
  },
  color: `${color}`, // テキストの色
  '@media (max-width: 600px)': {
    fontSize: '14px',
  },
})

export default FactorySelector
